import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { GitMerge, Settings, Trash2 } from 'lucide-react'
import HealthBadge from './HealthBadge'

const BUTTON_SPRING = { type: 'spring', stiffness: 400, damping: 20 } as const
const SURFACE_SPRING = { type: 'spring', stiffness: 500, damping: 30 } as const

interface ModeAction {
    active: boolean
    count: number | undefined
    onToggle: () => void
}

interface CollectionActionsMenuProps {
    /** Omitted when the viewer may not edit the collection's schema. */
    onEdit?: () => void
    /** Omitted when the collection is empty. */
    merge?: ModeAction
    prune?: ModeAction
}

/**
 * The People and Places pages' secondary actions — edit, merge, prune — behind
 * one gear, so the header keeps a single primary button (Add).
 *
 * The gear carries the merge and prune counts summed, so a collection with
 * something to clean up still says so with the menu shut.
 */
const CollectionActionsMenu: React.FC<CollectionActionsMenuProps> = ({ onEdit, merge, prune }) => {
    const [open, setOpen] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const onDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    if (!onEdit && !merge && !prune) return null

    const pending = (merge?.count ?? 0) + (prune?.count ?? 0)
    const active = !!(merge?.active || prune?.active)
    const pick = (fn: () => void) => () => {
        setOpen(false)
        fn()
    }
    const item = 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50'

    return (
        <div ref={rootRef} className="relative">
            <motion.button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="Collection settings"
                title="Collection settings"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                transition={BUTTON_SPRING}
                data-testid="collection-actions-menu"
                className={`relative p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-action cursor-pointer ${
                    active
                        ? 'bg-amber-100 text-amber-900'
                        : open
                            ? 'bg-secondary-dark text-gray-800'
                            : 'bg-secondary text-gray-700 hover:bg-secondary-dark'
                }`}
            >
                <Settings className="w-6 h-6" />
                {pending > 0 && (
                    <span
                        aria-hidden="true"
                        className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-danger ring-2 ring-white"
                    />
                )}
            </motion.button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -8 }}
                        transition={SURFACE_SPRING}
                        style={{ transformOrigin: 'top right' }}
                        role="menu"
                        className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                    >
                        {onEdit && (
                            <button type="button" role="menuitem" onClick={pick(onEdit)} className={item}>
                                <Settings className="w-4 h-4 text-gray-500" />
                                Edit collection
                            </button>
                        )}
                        {merge && (
                            <button type="button" role="menuitem" onClick={pick(merge.onToggle)} className={item}>
                                <GitMerge className="w-4 h-4 text-gray-500" />
                                <span className="flex-1">{merge.active ? 'Stop merging' : 'Merge duplicates'}</span>
                                <HealthBadge
                                    count={merge.count}
                                    label={`${merge.count} name collisions`}
                                    tone="amber"
                                />
                            </button>
                        )}
                        {prune && (
                            <button
                                type="button"
                                role="menuitem"
                                onClick={pick(prune.onToggle)}
                                data-testid="prune-toggle"
                                className={item}
                            >
                                <Trash2 className="w-4 h-4 text-gray-500" />
                                <span className="flex-1">{prune.active ? 'Stop pruning' : 'Prune unused'}</span>
                                <HealthBadge count={prune.count} label={`${prune.count} unreferenced items`} />
                            </button>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default CollectionActionsMenu
